import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

const scrollPositions = new Map<string, number>();

/**
 * Saves scroll position when leaving a page and restores it on back/forward navigation.
 * On PUSH navigation (clicking a link), scrolls to top.
 * Handles both window scrolling (public routes) and #main-content container (auth routes).
 */
export function ScrollRestoration() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const prevPath = useRef(pathname);

  // Detect the scroll target
  const getScrollTarget = () => {
    const el = document.getElementById("main-content");
    // Auth layout uses overflow-y-auto on #main-content
    return el && el.scrollHeight > el.clientHeight ? el : null;
  };

  const getScrollTop = () => {
    const el = getScrollTarget();
    return el ? el.scrollTop : window.scrollY;
  };

  const setScrollTop = (top: number) => {
    const el = getScrollTarget();
    if (el) {
      el.scrollTop = top;
    } else {
      window.scrollTo(0, top);
    }
  };

  // Save scroll position before navigating away
  useEffect(() => {
    if (prevPath.current !== pathname) {
      scrollPositions.set(prevPath.current, getScrollTop());
      prevPath.current = pathname;
    }
  }, [pathname]);

  // Restore or reset scroll position after navigation + animation
  useEffect(() => {
    // Wait for AnimatePresence enter animation to complete (~300ms)
    const timer = setTimeout(() => {
      if (navigationType === "POP") {
        const saved = scrollPositions.get(pathname);
        if (saved != null && saved > 0) {
          setScrollTop(saved);
        }
      } else {
        // PUSH / REPLACE → scroll to top
        setScrollTop(0);
      }
    }, 320);

    return () => clearTimeout(timer);
  }, [pathname, navigationType]);

  return null;
}
